import {
  FILE_TABLE_ACTION_DOWNLOAD,
  PDF,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { onPremHttpRequest } from "core/model/careseekers";
import { base64ToBytes } from "core/model/crypto";
import { createAndClickLink } from "core/model/files";
import { decryptFromSession } from "core/seald";
import { FileForm, FileHISStateful, Filev2, QueryProgress } from "core/types";
import {
  GeneralListAction,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { isFileHISStateful } from "dsl/organisms/FileTables";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { useFetchFormDataAndGeneratePdf } from "./useFetchFormDataAndGeneratePdf";
import { FileFeatureData } from "./useFilesFeature";
import { isFileForm } from "./utils";

type FileResult = {
  fileName: string;
  pdfBytes: Uint8Array;
  url: string;
};

export async function handleFileHISDownload({
  externalId,
  file,
  on_premise_authorization_token,
  on_premise_domain_ssl,
  onComplete,
  setProgress,
  target,
  toast,
  translations,
}: {
  externalId: string;
  file: FileHISStateful;
  on_premise_authorization_token: string | undefined;
  on_premise_domain_ssl: string | undefined;
  onComplete?: () => void;
  setProgress?: Dispatch<SetStateAction<QueryProgress>>;
  toast: ReturnType<typeof useToast>;
  translations: Translations;
  target: "_blank" | "_self" | undefined;
}): Promise<FileResult | undefined> {
  try {
    const response = await onPremHttpRequest({
      method: "GET",
      on_premise_authorization_token,
      on_premise_domain_ssl,
      path: `patients/${externalId}/documents/${file.document_id}`,
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch file: ${response.status} ${response.statusText}`,
      );
    }

    const blob = await response.blob();

    const url = createAndClickLink({
      blob,
      fileName: file.document_file_name,
      shouldClickLink: target !== undefined,
      target: target ?? "_blank",
    });

    const arrayBuffer = await blob.arrayBuffer();
    const pdfBytes = new Uint8Array(arrayBuffer);

    setProgress?.(QUERY_PROGRESS_SUCCEED);
    return {
      fileName: file.document_file_name,
      pdfBytes,
      url,
    };
  } catch (err) {
    console.error(err);
    setProgress?.(QUERY_PROGRESS_FAILED);
    toast({
      color: "danger",
      message: translations.fileSection.toastError,
    });
  } finally {
    onComplete?.();
  }
}

/**
 * @param target - The target of the link.
 * If "_blank", open in new browser tab (old behavior, still available if needed).
 * If "_self", forced download with correct filename (default for useDownloadFile).
 * If undefined, just create the blob URL, don't click anything (used by useCreateFileUrl for the PDF preview).
 * @returns A function that downloads a file.
 */
export const useHandleFileDownload = ({
  externalId,
  onComplete,
  setProgress,
  target,
}: {
  externalId: string;
  onComplete?: () => void;
  setProgress?: Dispatch<SetStateAction<QueryProgress>>;
  target: "_blank" | "_self" | undefined;
}): ((
  file: FileHISStateful | Filev2 | FileForm,
) => Promise<FileResult | undefined>) => {
  const toast = useToast();
  const { on_premise_authorization_token, on_premise_domain_ssl } =
    useHL7ImportStatusContext();
  const translations = useTranslations();

  const fetchFormDataAndGeneratePdf = useFetchFormDataAndGeneratePdf({
    externalId,
  });

  async function handleFileDownload({
    file,
  }: {
    file: Filev2;
  }): Promise<FileResult | undefined> {
    setProgress?.(QUERY_PROGRESS_PENDING);

    try {
      if (!file.link || !file.file_name?.seald_content || !file.file_type) {
        throw new Error(
          "[handleFileDownload] link, encrypted file_name and file type must be defined!",
        );
      }

      const response = await fetch(file.link, {
        headers: { Accept: "*/*" },
      });

      const text = await response.text();
      if (!text) {
        throw new Error("[handleFileDownload] Couldn't fetch encrypted file!");
      }

      const decryptedBase64File = await decryptFromSession({
        context: "file",
        encryptedMessage: text,
      });

      if (!decryptedBase64File) {
        throw new Error("[handleFileDownload] Couldn't decrypt file content!");
      }

      const fileName = await decryptFromSession({
        context: "[handleFileDownload] - file_name",
        encryptedMessage: file.file_name.seald_content,
      });

      if (!fileName) {
        throw new Error("[handleFileDownload] Couldn't decrypt file name!");
      }

      const url = createAndClickLink({
        blob: new Blob([base64ToBytes(decryptedBase64File) as BlobPart], {
          type: file.file_type,
        }),
        fileName,
        shouldClickLink: target !== undefined,
        target: target ?? "_blank",
      });
      setProgress?.(QUERY_PROGRESS_SUCCEED);
      return { fileName, pdfBytes: base64ToBytes(decryptedBase64File), url };
    } catch (err) {
      console.error(err);
      setProgress?.(QUERY_PROGRESS_FAILED);
      toast({
        color: "danger",
        message: translations.fileSection.toastError,
      });
    } finally {
      onComplete?.();
    }
  }

  return async (
    data: FileHISStateful | Filev2 | FileForm,
  ): Promise<FileResult | undefined> => {
    if (isFileHISStateful(data)) {
      return handleFileHISDownload({
        externalId,
        file: data,
        on_premise_authorization_token,
        on_premise_domain_ssl,
        onComplete,
        setProgress,
        target,
        toast,
        translations,
      });
    }

    if (isFileForm(data)) {
      setProgress?.(QUERY_PROGRESS_PENDING);

      try {
        const { fileName, pdfBytes } = await fetchFormDataAndGeneratePdf({
          file: data,
        });

        const url = createAndClickLink({
          blob: new Blob([pdfBytes], { type: PDF }),
          fileName,
          shouldClickLink: target !== undefined,
          target: target ?? "_blank",
        });
        setProgress?.(QUERY_PROGRESS_SUCCEED);
        return { fileName, pdfBytes, url };
      } catch (err) {
        console.error(err);
        setProgress?.(QUERY_PROGRESS_FAILED);
        toast({
          color: "danger",
          message: translations.fileSection.toastError,
        });
      } finally {
        onComplete?.();
      }
      return;
    }

    return handleFileDownload({ file: data });
  };
};

export function useDownloadFile({
  action,
  externalId,
  setAction,
  setProgress,
  target = "_self",
}: {
  action: GeneralListAction<FileHISStateful | Filev2 | FileForm> | undefined;
  externalId: string;
  setAction: SetAction<FileFeatureData>;
  setProgress: Dispatch<SetStateAction<QueryProgress>>;
  target?: "_blank" | "_self";
}) {
  const handleDownloadFile = useHandleFileDownload({
    externalId,
    onComplete: () => setAction(undefined),
    setProgress,
    target,
  });

  useEffect(() => {
    if (action?.type === FILE_TABLE_ACTION_DOWNLOAD) {
      handleDownloadFile(action.data);
    }
  }, [action?.type]);
}

export function useCreateFileUrl({
  externalId,
  onComplete,
  setProgress,
}: {
  externalId: string;
  onComplete?: () => void;
  setProgress?: Dispatch<SetStateAction<QueryProgress>>;
}) {
  const handleDownloadFile = useHandleFileDownload({
    externalId,
    onComplete,
    setProgress,
    target: undefined,
  });

  return async (
    file: FileHISStateful | Filev2 | FileForm,
  ): Promise<string | undefined> => {
    const result = await handleDownloadFile(file);
    return result?.url;
  };
}
